import { FC, useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { useActiveFeventListForWorkerHook } from '@shared/api/fevent/fevent.hook';
import { FeventList } from '@shared/api/fevent/fevent.interface';
import { activeFeventListForWorkerStateSelector } from '@shared/api/fevent/fevent.selector';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { searchTextAtomFamily } from '@shared/containers/SearchBar/state/searchBar.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import FuzzySearch from 'fuzzy-search';

import SearchBar from '../../../@shared/containers/SearchBar/SearchBar';
import { BugoCard } from '../components/BugoCard';
import { useTitleHook } from '../hooks/useTitleHook';

/**
 * 부고 관리 (리스트) 페이지
 * @returns page, container
 */

const SEARCH_TEXT_ID = 'bugoManagePage';

//* 전화번호 검색 최소 자리수 (뒷 4자리 검색 지원)
const PHONE_SEARCH_MIN_DIGITS = 4;

//* 상가 한 건에서 검색 대상이 되는 전화번호(상주 + 담당 지도사)를 숫자만 남겨 반환
const feventPhoneDigitsList = (fevent: FeventList[number]): string[] => {
  const rawList: (string | undefined)[] = (fevent.memberOrderList ?? []).map((member) =>
    typeof member === 'string' ? undefined : member.phoneNumber,
  );
  //* worker 조회에서는 user가 populate되지 않아 문자열(id)로 내려온다
  rawList.push(
    typeof fevent.user === 'string' ? undefined : fevent.user?.info.phoneNumber,
  );

  return rawList
    .map((phoneNumber) => (phoneNumber ?? '').replace(/[^0-9]/g, ''))
    .filter((digits) => digits.length > 0);
};

//*지도사용 부고관리 페이지
const FeventManagePage: FC = function FeventManagePage() {
  useTitleHook('부고 관리', undefined, undefined, true, '/');

  const { isAdmin } = useAuth();

  //* 지도사용
  useActiveFeventListForWorkerHook();

  const feventListForWorkerState = useRecoilValue(activeFeventListForWorkerStateSelector);

  const searchText = useRecoilValue(searchTextAtomFamily(SEARCH_TEXT_ID));

  const feventList = useMemo(() => {
    if (feventListForWorkerState.status === 'success' && feventListForWorkerState.data) {
      return feventListForWorkerState.data;
    } else return [];
  }, [feventListForWorkerState.data, feventListForWorkerState.status]);

  //* 첫 조회 전(data null)에만 로딩 표시. 창 포커스 refetch 등 재조회 땐 이전 목록을 그대로 두어 깜빡임 방지
  const isInitialLoading =
    feventListForWorkerState.data === null && feventListForWorkerState.status !== 'error';
  const isLoadError =
    feventListForWorkerState.data === null && feventListForWorkerState.status === 'error';

  //* 검색 결과는 파생값이므로 useMemo로 계산 (useState + useEffect 조합은 렌더 루프 유발)
  const searchedList: FeventList = useMemo(() => {
    // text가 빈 문자열이라면 feventList 전체 리턴.
    if (searchText.length === 0) return feventList;
    const searcher = new FuzzySearch(
      feventList,
      [
        'deceasedInfo.name',
        'memberOrderList.fullName',
        'memberOrderList.phoneNumber',
        'funeralHomeInfo.name',
        'roomInfoEmbed.name',
        'user.info.name',
        'user.info.phoneNumber',
      ],
      {
        caseSensitive: true,
      },
    );
    const fuzzyMatchedList = searcher.search(searchText);

    //* 전화번호 검색: 하이픈/공백을 무시하고 부분 일치 (이름 검색 결과에 덧붙임)
    const searchDigits = searchText.replace(/[^0-9]/g, '');
    if (searchDigits.length < PHONE_SEARCH_MIN_DIGITS) return fuzzyMatchedList;

    const fuzzyMatchedIdSet = new Set(fuzzyMatchedList.map((fevent) => fevent._id));
    const phoneMatchedList = feventList.filter(
      (fevent) =>
        !fuzzyMatchedIdSet.has(fevent._id) &&
        feventPhoneDigitsList(fevent).some((digits) => digits.includes(searchDigits)),
    );

    return [...fuzzyMatchedList, ...phoneMatchedList];
  }, [searchText, feventList]);

  return (
    <div className="px-4 pb-10">
      {/* Search Bar */}
      <div className="px-2 py-4">
        <SearchBar
          searchTextAtomId={SEARCH_TEXT_ID}
          placeholder="성함/전화번호/장례식장/호실로 검색하세요."
        />
      </div>
      {/* BugoCards */}
      {isInitialLoading ? (
        <div className="center-box h-screen-15">
          <Loading text="부고 목록을 불러오는 중" />
        </div>
      ) : isLoadError ? (
        <div className="center-box h-screen-15">
          <p className="text-sm text-gray-500">부고 목록을 불러오지 못했습니다.</p>
        </div>
      ) : searchedList.length === 0 ? (
        <div className="center-box py-16">
          <p className="text-sm text-gray-500">
            {searchText.length > 0
              ? '검색 결과가 없습니다.'
              : '진행 중인 부고가 없습니다.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {searchedList.map((fevent) => (
            <BugoCard fevent={fevent} key={'bugocard' + fevent._id} />
          ))}
        </div>
      )}
      {isAdmin() && (
        <div className="fixed bottom-20 right-10 flex flex-col items-center justify-center gap-4 rounded-lg border bg-white p-4">
          <Button
            className="shadow-sm"
            onClick={() => {
              window.scrollTo(0, 0);
            }}
          >
            top
          </Button>
          <Button
            className="shadow-sm"
            onClick={() => {
              window.scrollTo(0, document.body.scrollHeight);
            }}
          >
            bot
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeventManagePage;
